import React from "react";
import { RiCloseLine } from "react-icons/ri";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import Badge from "UpdatedFeatures/Badge";
import { Puff } from "react-loader-spinner";
import { IoDocumentAttach } from "react-icons/io5";
import { ChangeStatusClientPRO, ReactivateClientPRO } from "UpdatedFeatures/api";

const ClientStatusModal = ({ open, setOpen, selectedClient }) => {
  const queryClient = useQueryClient();

  const statusMutation = useMutation({
    mutationFn: ChangeStatusClientPRO,
    onSuccess: () => {
      toast.success("Statut du client mis à jour avec succès");
      queryClient.invalidateQueries(["clients-pro"]);
      setOpen(false);
    },
    onError: (error) => {
      toast.error(error.message || "Erreur lors de la mise à jour du statut");
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: ReactivateClientPRO,
    onSuccess: () => {
      toast.success("Statut du client mis à jour avec succès");
      queryClient.invalidateQueries(["clients-pro"]);
      setOpen(false);
    },
    onError: (error) => {
      toast.error(error.message || "Erreur lors de la mise à jour du statut");
    },
  });

  if (!open || !selectedClient) return null;

  const getAvailableActions = (currentStatus) => {
    switch (currentStatus) {
      case 0: // En attente
        return [
          { status: 1, label: "Approuver", color: "blue" },
          { status: 3, label: "Rejeter", color: "red" },
        ];
      case 1: // Approuvé
        return [
          { status: 3, label: "Désactiver", color: "red" },
        ];
      case 2: // Actif
        return [
          { status: 3, label: "Désactiver", color: "red" },
        ];
      case 3: // Désactivé
        return [
          { status: null, label: "Re-Activer", color: "green" },
        ];
      default:
        return [];
    }
  };
  const BASE_URL = process.env.REACT_APP_API_ENDPOINT;
  const documentUrl = `${BASE_URL}storage/${selectedClient.registration_document}`;
  console.log(selectedClient.registration_document)
  const colorMap = {
    "blue": "text-blue-500 bg-blue-500 bg-opacity-10 border border-blue-500/30",
    "red": "text-red-500 bg-red-500 bg-opacity-10 border border-red-500/30",
    "green": "text-green-500 bg-green-500 bg-opacity-10 border border-green-500/30",
  };

  const handleStatusChange = (newStatus) => {
    console.log(newStatus)
    if (!newStatus)
      reactivateMutation.mutate({ clientId: selectedClient.id });
    else
      statusMutation.mutate({ clientId: selectedClient.id, status: newStatus });
  };

  const availableActions = getAvailableActions(selectedClient.status);

  return (
    <div
      className="fixed inset-0 z-50"
      style={{ zIndex: 9991, overflow: "auto" }}
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-gray-500 bg-opacity-75"
        aria-hidden="true"
        onClick={() => setOpen(false)}
      />

      {/* Modal Content */}
      <div className="flex items-center justify-center min-h-screen p-4">
        <div
          className="relative w-full max-w-3xl mx-auto bg-white shadow-lg rounded-lg overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex justify-between items-center p-6 bg-[#b269ae] border-b border-gray-500/30">
            <h3 id="modal-title" className="text-lg font-medium leading-6 text-white">
              Gestion du Statut Client PRO
            </h3>
            <button
              type="button"
              className="inline-flex items-center justify-center p-1.5 text-white hover:text-gray-200"
              onClick={() => setOpen(false)}
            >
              <RiCloseLine className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>

          <div className="p-6">
            {/* Client Information */}
            <div className="bg-gray-50 border p-4 rounded-lg mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Raison sociale</h4>
                  <p className="font-semibold text-lg">{selectedClient.company_name}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Statut actuel</h4>
                  <Badge status={selectedClient.status} type="clients" />
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Dirigeant</h4>
                  <p>{selectedClient.first_name} {selectedClient.last_name}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Email</h4>
                  <p>{selectedClient.email}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Téléphone</h4>
                  <p>{selectedClient.phone}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Localisation</h4>
                  <p>{selectedClient.country}, {selectedClient.city} , {selectedClient.adress}, {selectedClient.zip_code}</p>
                </div>
              </div>
              <a href={documentUrl} target="_blank" className="mt-4 flex justify-center items-center gap-2 bg-yellow-400 text-white p-2 rounded-full">
                <IoDocumentAttach size={20} />
                Voir le document d’immatriculation de la société
              </a>
            </div>

            {/* Status Actions */}
            <div className="space-y-4">
              <h4 className="text-md font-semibold text-gray-800 pb-2 border-b">
                Actions disponibles
              </h4>

              {availableActions.length > 0 ? (
                <div className="flex flex-wrap gap-3">
                  {availableActions.map((action) => (
                    <button
                      key={action.status}
                      onClick={() => handleStatusChange(action.status)}
                      disabled={statusMutation.isLoading}
                      className={`px-4 py-2 rounded-md font-medium transition-all duration-300 
                      ${colorMap[action.color] || "text-gray-500 bg-gray-500 bg-opacity-10 border border-gray-500"} 
                      hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2`}
                    >
                      {statusMutation.isLoading ? <Puff color="white" height={16} width={16} /> : null}
                      {action.label}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 italic">
                  Aucune action disponible pour ce statut
                </p>
              )}
            </div>

            {/* Additional Information or History could be added here */}

            {/* Footer */}
            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientStatusModal; 